/**
 * The numbers and names Today, Weather, Seasons, the tips and the welcome are
 * tuned by.
 *
 * Kept in one place, out of the components, so that each can be found and
 * changed without reading the screen that uses it, and so that they can later
 * come from the database instead (each carries its kept-values group, scripts/kept-values.md). Constants
 * only: no functions.
 *
 * Not here: what the owner already changes in the Grow settings panel
 * (src/shared/lib/growSettings: Coming up's days, the countdown, the rain
 * month, the weather thresholds and the rest), and the app-wide query
 * defaults (src/config/queryClient), which these screens reuse.
 *
 * Used by: features/today, weather, season (the Today query keys and
 * retries), tips and welcome, and PacketReview's planting dates. What's
 * ahead's own numbers (the moon look-ahead, the most planting lines, the
 * "plants added" grouping) moved to the backend with the decisions
 * (config/tunables/season there), and the plant cards' planting-date
 * numbers (how soon a window is said, how a long window is rounded to its
 * months, when a Seeding plant was started indoors) with theirs
 * (config/tunables/plants and today there; D40).
 *
 * @module tunables/today
 */

// ---------------------------------------------------------------------------
// Today
// ---------------------------------------------------------------------------

/**
 * The React Query key the server's day (getToday) is cached under, followed
 * by the postcode and the date. Today, Today's picture setting and Seasons'
 * Recent and coming up read the same day, and the picture setting invalidates it.
 * @kept K1
 */
export const TODAY_QUERY_KEY = 'today';

/**
 * The React Query key the Today brief (getTodayBrief) is cached under,
 * followed by the garden, the language, the unit and Coming up's days
 * (lib/briefFreshness briefKey, useTodayBrief). Today, the Plants, Beds and
 * Weather pages and the header's frost bar read the same brief; a bed or
 * plant saved invalidates it (gardenWritten).
 * @kept K1
 */
export const TODAY_BRIEF_QUERY_KEY = 'today-brief';

/**
 * How many times Today's, Seasons' and Weather's own reads (the day, the sky,
 * the frost dates, a postcode check) are tried again after a failure before
 * the page goes without them. A count.
 * @kept K1
 */
export const TODAY_QUERY_RETRIES = 1;

/**
 * How long a mark waits before its one more read of the Today brief, when the
 * read after the mark failed (B49, useTodoMarks): long enough for a slow
 * server to finish the build the first read started. Milliseconds.
 * @kept K1
 */
export const TODO_BRIEF_RETRY_MS = 5000;

/**
 * The localStorage key the month's opening days are kept under, for when the
 * wettest or driest month is said (useRainMonth).
 * @kept K1
 */
export const RAIN_MONTH_STORAGE_KEY = 'grow.today.rainMonthDays';

// ---------------------------------------------------------------------------
// Planting dates (Planning plants)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Weather
// ---------------------------------------------------------------------------

/**
 * How long the frost card is followed down a page still filling in, after
 * arriving by a link to the frost dates, unless the gardener scrolls or taps
 * first. Milliseconds.
 * @kept K5
 */
export const FROST_ARRIVAL_FOLLOW_MS = 8000;

// ---------------------------------------------------------------------------
// The welcome
// ---------------------------------------------------------------------------

/**
 * The longest Today waits for the tips before choosing between the welcome
 * and Today itself, so a tips request that never answers cannot leave the
 * loader up. Usually they arrive in well under a second.
 * Unit: ms
 */
export const TODAY_SETTLE_MAX_MS = 4000;

/**
 * The longest Today's loader holds, after the plants list shows a plant, for the
 * brief to drop its stale empty-garden read (B30 rev 6), so a brief that never
 * arrives cannot leave the loader up. The brief can take well over ten seconds
 * on a slow server.
 * Unit: ms
 * @kept K1
 */
export const TODAY_FIRST_PLANT_HOLD_MAX_MS = 60000;

/**
 * The shape of Today's picture strip, width / height. 10:1 cut the gardener
 * out of every painting (owner, 2026-10-09); 4:1 holds her head and
 * shoulders with the crops the server sends (today.STRIP_<key>, painting.focus).
 * @kept K5
 */
export const TODAY_STRIP_ASPECT = '4 / 1';

/**
 * How long the Today brief waits before it is read again while the server
 * says it is rebuilding the garden's outlook (the brief's `updating`), in
 * milliseconds.
 * @kept K1
 */
export const UPDATING_POLL_MS = 4000;

/**
 * How many times in a row the brief is read again on the server's word that
 * it is still updating, so a loader can never be stuck (about two minutes).
 * A count.
 * @kept K1
 */
export const UPDATING_POLLS = 30;
